(function (global) {
  'use strict';

  class PMSShopifyAdapter {
    constructor(rootElement, config = {}) {
      this.rootElement = rootElement;
      this.config = config;
    }

    readJson(selector) {
      const node = this.rootElement.querySelector(selector);
      if (!node) return null;
      try {
        return JSON.parse(node.textContent);
      } catch (error) {
        console.error('[PMS Shopify Adapter] Invalid embedded JSON.', error);
        return null;
      }
    }

    normalizeProduct(raw) {
      if (!raw) return null;
      return {
        id: raw.id == null ? null : String(raw.id),
        handle: raw.handle || '',
        title: raw.title || '',
        options: (raw.options || []).map((option) =>
          typeof option === 'string' ? option : option?.name || ''
        ),
        variants: (raw.variants || []).map((variant) => ({
          id: String(variant.id),
          title: variant.title || '',
          options: Array.isArray(variant.options)
            ? variant.options.map((value) => String(value))
            : [variant.option1, variant.option2, variant.option3]
                .filter((value) => value != null)
                .map((value) => String(value)),
          price: Number(variant.price) || 0,
          available: Boolean(variant.available),
          inventoryQuantity:
            variant.inventoryQuantity ?? variant.inventory_quantity ?? null,
          inventoryPolicy:
            variant.inventoryPolicy ?? variant.inventory_policy ?? null,
          inventoryManagement:
            variant.inventoryManagement ?? variant.inventory_management ?? null,
          featuredMediaId:
            variant.featuredMediaId ?? variant.featured_media_id ?? variant.featured_image?.id ?? null,
          sku: variant.sku || '',
        })),
      };
    }

    async loadProducts() {
      const embedded = this.readJson('[data-pms-catalog]');
      if (embedded?.coat && embedded?.pant) {
        const [coat, pant, vest] = await Promise.all([embedded.coat, embedded.pant, embedded.vest].map(raw => this.loadCompleteProduct(raw)));
        return {
          coat, pant, vest,
        };
      }

      const requests = [
        this.fetchProduct(this.config.coatHandle),
        this.fetchProduct(this.config.pantHandle),
      ];
      if (this.config.vestHandle) requests.push(this.fetchProduct(this.config.vestHandle));
      const [coat, pant, vest] = await Promise.all(requests);
      return {
        coat: this.normalizeProduct(coat),
        pant: this.normalizeProduct(pant),
        vest: this.normalizeProduct(vest),
      };
    }

    async loadCompleteProduct(raw) {
      if (!raw) return null;
      if (!raw.variantsCount || raw.variants.length === raw.variantsCount) return this.normalizeProduct(raw);
      const root = global.Shopify?.routes?.root || '/';
      let url = new URL(`${root}products/${encodeURIComponent(raw.handle)}?view=pms-suit-data`, global.location.origin);
      const pathname = url.pathname;
      const variants = new Map();
      const visited = new Set();
      let expected = raw.variantsCount;
      while (url) {
        if (url.origin !== global.location.origin || url.pathname !== pathname || visited.has(url.href) || visited.size >= 10) throw new Error('Invalid suit catalog page.');
        visited.add(url.href);
        const response = await fetch(url.href, { headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error('The full suit size list could not be loaded.');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const node = html.querySelector('[data-pms-product-data]');
        if (!node) throw new Error('Missing suit catalog data.');
        const page = JSON.parse(node.textContent);
        if (String(page.id) !== String(raw.id) || !Array.isArray(page.variants)) throw new Error('Unexpected suit catalog product.');
        expected = page.variantsCount;
        page.variants.forEach(v => variants.set(String(v.id), v));
        url = page.paginationNext ? new URL(page.paginationNext, global.location.origin) : null;
      }
      if (variants.size !== expected) throw new Error('The full suit size list could not be verified. Please refresh.');
      return this.normalizeProduct({ ...raw, variants: [...variants.values()] });
    }

    async fetchProduct(handle) {
      if (!handle) return null;
      const root = global.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}products/${encodeURIComponent(handle)}.js`, {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error(`Unable to load product ${handle}.`);
      return response.json();
    }

    toShopifyItem(item) {
      const meta = item.metadata || {};
      const properties = {
        _pms_suit_set: meta.groupId || '',
        _pms_suit_program: meta.programTitle || '',
        _pms_suit_color: meta.colorLabel || '',
        _pms_suit_color_key: meta.colorKey || '',
        _pms_suit_component: item.component || '',
      };
      if (meta.secondPant) properties._pms_second_matching_pant = 'Yes';

      return {
        id: item.variantId,
        quantity: item.quantity,
        properties,
      };
    }

    async addCartPlan(plan, afterAdd = 'drawer') {
      const cartUI = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      const payload = { items: plan.items.map((item) => this.toShopifyItem(item)) };

      if (afterAdd === 'drawer' && cartUI?.getSectionsToRender) {
        const sections = cartUI.getSectionsToRender().map((section) => section.id).filter(Boolean);
        if (sections.length) {
          payload.sections = sections;
          payload.sections_url = global.location.pathname;
        }
      }

      const root = global.Shopify?.routes?.root || '/';
      const endpoint = global.routes?.cart_add_url || `${root}cart/add.js`;
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.description || data.message || 'The selections could not be added.');
      }

      if (afterAdd === 'drawer' && cartUI?.renderContents && data.sections) {
        // Match the theme's native product form when the first items enter an empty cart.
        cartUI.classList.remove('is-empty');
        data.id = data.id || data.items?.[0]?.key || data.items?.[0]?.id;
        cartUI.renderContents(data);
      } else {
        global.location.assign(`${root}cart`);
      }
      return data;
    }
  }

  global.PMSShopifyAdapter = PMSShopifyAdapter;
})(typeof window !== 'undefined' ? window : globalThis);
